import type { ReactElement } from 'react';
import type { ProductKind } from 'types';
import { ProductKindSection } from '../../products/ProductKindSection';
import { getProductListColumns } from '../../products/productListColumns';
import { productsByKind, type ProductKindSectionMeta } from '../../products/kindSections';
import type { ProductAuthoringProduct } from './types';

export type AuthoringKindSectionsProps<TProduct extends ProductAuthoringProduct> = {
  products: TProduct[];
  prices: Partial<Record<string, string>>;
  sections: ProductKindSectionMeta[];
  onEdit?: (productId: number) => void;
  onCreate?: (kind: ProductKind) => void;
};

/**
 * The default flat kind-section composition shared by scalar-priced
 * workspaces: every section renders its products with outlet, list price,
 * fallback price, and state columns. The owning workspace selects the
 * sections (RADIO passes only SERVICE and SMART) and keeps any type-owned
 * replacements outside this component.
 */
export function AuthoringKindSections<TProduct extends ProductAuthoringProduct>({
  products,
  prices,
  sections,
  onEdit,
  onCreate,
}: AuthoringKindSectionsProps<TProduct>): ReactElement {
  const groupedProducts = productsByKind(products);
  return (
    <>
      {sections.map((meta) => {
        const sectionProducts = groupedProducts.get(meta.kind) ?? [];
        return (
          <ProductKindSection
            key={meta.kind}
            meta={meta}
            count={sectionProducts.length}
            onCreate={onCreate ? () => onCreate(meta.kind) : undefined}
            rows={sectionProducts.map((product) => ({ product, listPrice: prices[product.id] ?? null }))}
            columns={getProductListColumns<TProduct>({
              onOpen: onEdit ? (product) => onEdit(product.id) : undefined,
              showOutlet: true,
              showListPrice: true,
              kind: meta.kind,
            })}
            getRowId={({ product }) => String(product.id)}
          />
        );
      })}
    </>
  );
}
